import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useClusterDetail, useTriggerBackup } from "@/hooks/use-backup";
import { Badge, Button, Card, CardContent, Collapsible, CollapsibleContent, CollapsibleTrigger, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@fox1ck-org/ui";

function formatTime(iso?: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60_000) return "только что";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} мин назад`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} ч назад`;
  return d.toLocaleDateString();
}

function formatDuration(start?: string, end?: string) {
  if (!start || !end) return "-";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return "<1s";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function PhaseBadge({ phase }: { phase: string }) {
  const isHealthy = phase.toLowerCase().includes("healthy") || phase === "completed";
  const isFailed = phase.toLowerCase().includes("fail") || phase.toLowerCase().includes("error");
  const variant = isHealthy ? "ok" as const : isFailed ? "danger" as const : "warn" as const;
  return <Badge variant={variant}>{phase}</Badge>;
}

function InfoCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-sm text-foreground font-medium break-all">{value ?? "-"}</p>
      </CardContent>
    </Card>
  );
}

export default function ClusterDetail() {
  const { ns, name } = useParams<{ ns: string; name: string }>();
  const { data, isLoading, error } = useClusterDetail(ns!, name!);
  const triggerBackup = useTriggerBackup();
  const [pgConfigOpen, setPgConfigOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
        </div>
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/backups"><ArrowLeft className="h-4 w-4" /> К резервным копиям</Link>
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          Не удалось загрузить кластер: {error?.message ?? "Не найдено"}
        </div>
      </div>
    );
  }

  const { cluster, backups, scheduledBackup } = data;
  const pgParams = cluster.postgresParameters ?? {};
  const pgParamEntries = Object.entries(pgParams);
  const certExpirations = cluster.certificateExpirations ?? {};
  const certEntries = Object.entries(certExpirations);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/backups"><ArrowLeft className="h-5 w-5" /></Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[17px] font-semibold text-foreground">{cluster.name}</h1>
              <PhaseBadge phase={cluster.phase} />
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{cluster.namespace}</p>
          </div>
        </div>
        <Button
          onClick={() => triggerBackup.mutate(
            { ns: ns!, name: name! },
            { onSuccess: () => toast.success("Резервная копия запущена"), onError: (e) => toast.error(e.message) }
          )}
          disabled={triggerBackup.isPending}
        >
          {triggerBackup.isPending ? "Создаём…" : "Создать копию"}
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <InfoCard label="Инстансы" value={<>{cluster.readyInstances}/{cluster.instances} ready{cluster.healthyInstances && <span className="block text-xs text-muted-foreground mt-0.5">{cluster.healthyInstances.join(", ")}</span>}</>} />
        <InfoCard label="Хранилище" value={cluster.storageSize ?? "-"} />
        <InfoCard label="База / владелец" value={`${cluster.databaseName ?? "-"} / ${cluster.dbOwner ?? "-"}`} />
        <InfoCard label="Образ Postgres" value={cluster.image ? cluster.image.split("/").pop() : "-"} />
        <InfoCard label="Основной под" value={cluster.primaryPod ?? "-"} />
        <InfoCard label="Таймлайн WAL" value={cluster.timelineID != null ? `#${cluster.timelineID}` : "-"} />
      </div>

      {/* Backup Status */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-3">Статус копий</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <InfoCard label="Восстановимо с" value={formatTime(cluster.firstRecoverabilityPoint)} />
          <InfoCard label="Последняя копия" value={formatTime(cluster.lastBackupTime)} />
          <InfoCard label="Путь S3" value={cluster.s3DestinationPath ?? "-"} />
          <InfoCard label="Хранение" value={cluster.retentionPolicy ?? "-"} />
          <InfoCard label="Расписание" value={scheduledBackup ? scheduledBackup.schedule : "-"} />
          <InfoCard label="Следующий / последний запуск" value={scheduledBackup ? `${formatTime(scheduledBackup.nextScheduleTime)} / ${formatTime(scheduledBackup.lastScheduleTime)}` : "-"} />
        </div>
      </div>

      {/* Conditions */}
      {cluster.conditions.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-3">Условия</h2>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Тип</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Причина</TableHead>
                  <TableHead>Сообщение</TableHead>
                  <TableHead>Последний переход</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cluster.conditions.map((cond) => (
                  <TableRow key={cond.type}>
                    <TableCell className="font-medium">{cond.type}</TableCell>
                    <TableCell>
                      <span className={`inline-block h-2.5 w-2.5 rounded-full mr-2 ${cond.status === "True" ? "bg-green-400" : "bg-red-400"}`} />
                      {cond.status === "True" ? "Да" : "Нет"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{cond.reason ?? "-"}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">{cond.message ?? "-"}</TableCell>
                    <TableCell className="text-muted-foreground">{formatTime(cond.lastTransitionTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Backup History */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-3">История копий</h2>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Название</TableHead>
                <TableHead>Фаза</TableHead>
                <TableHead>Под инстанса</TableHead>
                <TableHead>Запущено</TableHead>
                <TableHead>Завершено</TableHead>
                <TableHead>Длительность</TableHead>
                <TableHead>Назначение</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {backups.map((b) => (
                <TableRow key={b.name}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell><PhaseBadge phase={b.phase} /></TableCell>
                  <TableCell className="text-muted-foreground">{b.instancePod ?? "-"}</TableCell>
                  <TableCell className="text-muted-foreground">{formatTime(b.startedAt)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatTime(b.completedAt)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDuration(b.startedAt, b.completedAt)}</TableCell>
                  <TableCell className="text-muted-foreground max-w-xs truncate">{b.destinationPath ?? "-"}</TableCell>
                </TableRow>
              ))}
              {backups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">Копий нет</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Postgres Config */}
      {pgParamEntries.length > 0 && (
        <Collapsible open={pgConfigOpen} onOpenChange={setPgConfigOpen}>
          <CollapsibleTrigger className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2 hover:text-muted-foreground cursor-pointer">
            Настройки Postgres
            <span className="text-sm text-muted-foreground">{pgConfigOpen ? "[-]" : `[+] параметров: ${pgParamEntries.length}`}</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card>
              <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                {pgParamEntries.map(([key, val]) => (
                  <div key={key} className="text-sm">
                    <span className="text-muted-foreground">{key}:</span>{" "}
                    <span className="text-foreground">{val}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Connectivity */}
      {(cluster.writeService || cluster.readService || certEntries.length > 0) && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-3">Подключение</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cluster.writeService && <InfoCard label="Сервис записи" value={cluster.writeService} />}
            {cluster.readService && <InfoCard label="Сервис чтения" value={cluster.readService} />}
          </div>
          {certEntries.length > 0 && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
              {certEntries.map(([certName, expiry]) => (
                <InfoCard key={certName} label={`Сертификат: ${certName}`} value={formatTime(expiry)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
